import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';

import {
  IAccount,
  InternalAccountsService
} from './../services/internal-accounts.service';

import TRANSFER_TYPE from './../enums/transfer-type';
import { IBaseAccount, ToAccountsListItemType } from './to-accounts.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

enum IState {
  internalAccounts = 'INTERNAL_ACCOUNTS',
  toAccounts = 'TO_ACCOUNTS',
  fromAccount = 'FROM_ACCOUNT',
  toAccount = 'TO_ACCOUNT',
  previousState = 'PREVIOUS_STATE',
  internationalTransfer = 'INTERNATIONAL_TRANSFER',
  editTransfer = 'EDIT_TRANSFER',
  prefilledData = 'PREFILLED_DATA',
  domesticBeneficiaryBank = 'DOMESTIC_BENEFICIARY_BANK'
}

interface IFinalState {
  transferType: TRANSFER_TYPE;
  data: any;
}

interface ITransferState {
  internalAccounts: BehaviorSubject<Array<IAccount>>;
  toAccount: BehaviorSubject<IAccount>;
  toAccounts: BehaviorSubject<Array<IAccount>>;
  fromAccount: BehaviorSubject<any>;
  finalState: BehaviorSubject<IFinalState>;
  internationalTransfer: BehaviorSubject<any>;
  domesticBeneficiaryBank: BehaviorSubject<any>;
}

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private fromAccount: Subscription;
  private toAccount: Subscription;
  private fromEditInit = false;
  private toEditInit = false;

  private state: ITransferState = {
    internalAccounts: new BehaviorSubject<Array<IAccount>>([]),
    toAccount: new BehaviorSubject<IAccount>({}),
    toAccounts: new BehaviorSubject<Array<IAccount>>([]),
    fromAccount: new BehaviorSubject<any>({}),
    finalState: new BehaviorSubject<IFinalState>({
      transferType: TRANSFER_TYPE.default,
      data: null
    }),
    internationalTransfer: new BehaviorSubject<any>(false),
    domesticBeneficiaryBank: new BehaviorSubject<any>({})
  };

  private previousState: IFinalState = {
    transferType: TRANSFER_TYPE.default,
    data: null
  };

  constructor(private internalAccountsService: InternalAccountsService) {}

  resetFormMode() {
    this.state.finalState = new BehaviorSubject<IFinalState>({
      transferType: TRANSFER_TYPE.default,
      data: null
    });
    this.previousState = {
      transferType: TRANSFER_TYPE.default,
      data: null
    };

    this.state[IState.editTransfer] = new BehaviorSubject<any>({});
    this.state[IState.prefilledData] = new BehaviorSubject<any>({});

    if (this.fromAccount && this.toAccount) {
      this.fromAccount.unsubscribe();
      this.fromAccount = undefined;
      this.toAccount.unsubscribe();
      this.toAccount = undefined;
    }
  }

  instantiateProp(prop: IState): void {
    const DEFAULT_PROP_MAP = {
      INTERNAL_ACCOUNTS: [],
      TO_ACCOUNTS: []
    };

    this.state[prop] = new BehaviorSubject<any>(DEFAULT_PROP_MAP[prop] || {});
  }

  setProp(prop: IState, state: any, stopSetState?: boolean): void {
    if (!this.state[prop]) {
      this.instantiateProp(prop);
    }

    this.state[prop].next(state);
    if (!stopSetState) {
      this.setState();
    }
  }

  getProp(prop: IState): Observable<any> {
    if (!this.state[prop]) {
      this.instantiateProp(prop);
    }

    return this.state[prop].asObservable();
  }

  getPropValue(prop: IState): BehaviorSubject<any> {
    if (!this.state[prop]) {
      this.instantiateProp(prop);
    }

    return this.state[prop];
  }

  setState() {
    let computedState: IFinalState;

    switch (true) {
      case this.isPrefilledForm():
        computedState = this.getPropValue(IState.editTransfer).value;
        break;

      case this.isInternationalTransfer():
        computedState = {
          transferType: TRANSFER_TYPE.internationalTransfer,
          data: null
        };
        break;

      case this.isInternalTransferOnly():
        computedState = {
          transferType: TRANSFER_TYPE.internalTransfer,
          data: {
            internalOnly: true
          }
        };
        break;

      case this.isBillPayeeTransfer():
        computedState = {
          transferType: TRANSFER_TYPE.billPayee,
          data: null
        };
        break;

      case this.isDomesticTransfer():
        computedState = {
          transferType: TRANSFER_TYPE.domesticTransfer,
          data: null
        };
        break;

      case this.isInternalTransfer():
        computedState = {
          transferType: TRANSFER_TYPE.internalTransfer,
          data: {
            internalOnly: false
          }
        };
        break;

      default:
        computedState = {
          transferType: TRANSFER_TYPE.default,
          data: null
        };
        break;
    }

    if (
      this.previousState.transferType !== computedState.transferType ||
      JSON.stringify(this.previousState.data) !==
        JSON.stringify(computedState.data)
    ) {
      this.previousState = computedState;
      this.state.finalState.next(computedState);
    }

    return this.state.finalState;
  }

  getState(): Observable<IFinalState> {
    return this.state.finalState
      .asObservable()
      .pipe(debounceTime(250), distinctUntilChanged());
  }

  forceState(state: IFinalState): void {
    this.previousState = state;
    this.state.finalState.next(state);
  }

  isInternalTransferOnly(): boolean {
    const fromAccount = this.getPropValue(IState.fromAccount).value as IAccount;

    return (
      (fromAccount.transferOptions &&
        this.internalAccountsService.isOnly(
          fromAccount.transferOptions,
          'internalTransferAllowed'
        )) ||
      (fromAccount.accountCreateTransfersPermissions &&
        fromAccount.accountCreateTransfersPermissions.internalTransferEnabled &&
        !fromAccount.accountCreateTransfersPermissions
          .domesticTransferEnabled &&
        !fromAccount.accountCreateTransfersPermissions.billTransferEnabled)
    );
  }

  isInternalTransfer(): boolean {
    const fromAccount = this.getPropValue(IState.fromAccount).value as IAccount;
    const internalAccounts = this.getPropValue(IState.internalAccounts)
        .value as Array<IAccount>,
      toAccounts = this.getPropValue(IState.toAccounts).value as Array<
        IAccount
      >,
      toAccount = this.getPropValue(IState.toAccount).value as IBaseAccount;

    return (
      (ToAccountsListItemType[toAccount.listItemType] &&
        ToAccountsListItemType[toAccount.listItemType] ===
          ToAccountsListItemType.OwnAccount) ||
      internalAccounts.some(account => {
        return (
          account.accountNumber === toAccount.accountNumber &&
          account.accountNumber !== fromAccount.accountNumber
        );
      }) ||
      (toAccounts.length &&
        toAccounts.some(account => {
          return (
            account.accountNumber === toAccount.accountNumber &&
            account.accountNumber !== fromAccount.accountNumber
          );
        }))
    );
  }

  isDomesticTransfer(): boolean {
    const toAccount = this.getPropValue(IState.toAccount).value as IBaseAccount,
      internalAccounts = this.getPropValue(IState.internalAccounts)
        .value as Array<IAccount>,
      toAccounts = this.getPropValue(IState.toAccounts).value as Array<
        IAccount
      >,
      domesticBeneficiaryBank = this.getPropValue(
        IState.domesticBeneficiaryBank
      ).value;
    return (
      (ToAccountsListItemType[toAccount.listItemType] &&
        ToAccountsListItemType[toAccount.listItemType] !==
          ToAccountsListItemType.OwnAccount) ||
      (!this.isInternalTransferOnly() &&
        internalAccounts.every(account => {
          return account.accountNumber !== toAccount.accountNumber;
        }) &&
        toAccounts.every(account => {
          return account.accountNumber !== toAccount.accountNumber;
        })) ||
      (Object.entries(domesticBeneficiaryBank).length &&
        ToAccountsListItemType[toAccount.listItemType] &&
        ToAccountsListItemType[toAccount.listItemType] !==
          ToAccountsListItemType.OwnAccount &&
        (internalAccounts.some(account => {
          return account.accountNumber === toAccount.accountNumber;
        }) ||
          toAccounts.some(account => {
            return account.accountNumber === toAccount.accountNumber;
          })))
    );
  }

  isBillPayeeTransfer(): boolean {
    const toAccount = this.getPropValue(IState.toAccount).value as IBaseAccount;

    return (
      ToAccountsListItemType[toAccount.listItemType] &&
      ToAccountsListItemType[toAccount.listItemType] ===
        ToAccountsListItemType.BillPayee
    );
  }

  private isInternationalTransfer(): boolean {
    return this.getPropValue(IState.internationalTransfer).value;
  }

  private isPrefilledForm(): boolean {
    let prefilledForm = false;

    const editTransferState = this.getPropValue(IState.editTransfer).value;
    const emptyState = Object.keys(editTransferState).length === 0;

    if (
      editTransferState &&
      (emptyState ||
        (editTransferState.data && editTransferState.data.prefilledForm))
    ) {
      prefilledForm = true;

      if (!this.fromAccount && !this.toAccount && !emptyState) {
        const prefilledData = this.getPropValue(IState.prefilledData).value;
        if (Object.keys(prefilledData).length === 0) {
          return;
        }

        let toAccount;

        try {
          toAccount =
            prefilledData.toAccount.accountNumber || prefilledData.toAccount;
        } catch (error) {
          console.error(error);
          return;
        }

        this.fromAccount = this.getProp(IState.fromAccount).subscribe(value => {
          if (Object.keys(value).length === 0) {
            return;
          }

          if (!this.fromEditInit) {
            this.fromEditInit =
              value.accountNumber === prefilledData.fromAccount;
          }

          if (
            this.fromEditInit &&
            value.accountNumber !== prefilledData.fromAccount
          ) {
            this.stopPrefillMode();
          }
        });

        this.toAccount = this.getProp(IState.toAccount).subscribe(value => {
          if (!value.accountNumber) {
            return;
          }

          if (!this.toEditInit) {
            this.toEditInit = value.accountNumber === toAccount;
          }

          if (this.toEditInit && value.accountNumber !== toAccount) {
            this.stopPrefillMode();
          }
        });
      }
    }

    return prefilledForm;
  }

  stopPrefillMode() {
    if (
      this.getPropValue(IState.editTransfer).value.transferType ===
      TRANSFER_TYPE.internationalTransfer
    ) {
      this.setProp(IState.internationalTransfer, true, true);
    }
    this.setProp(IState.editTransfer, { data: { prefilledForm: false } });
    this.setProp(IState.prefilledData, null, true);
    if (this.fromAccount) {
      this.fromAccount.unsubscribe();
    }

    if (this.toAccount) {
      this.toAccount.unsubscribe();
    }
  }
}

export { IState, IFinalState };
